import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react'
import { useToast } from '@chakra-ui/react'
import { useMutation, useQueryClient } from 'react-query'
import { Capacitor } from '@capacitor/core'
import { putProfileImage } from '../api/user/home/home'
import { useCamera } from '../components/modals/CommunityImageSelectModalModal'
import { isMediaPermissionDeniedError } from '../components/modals/MediaPermissionRequiredModal'
import handleToast from '../components/toast/handleToast'

export const useProfilePhotoUpload = ({ onUploaded }: { onUploaded?: () => void } = {}) => {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const [selectedImageWebPath, setSelectedImageWebPath] = useState<string | undefined>(undefined)
  const [selectedImageObjectUrl, setSelectedImageObjectUrl] = useState<string | undefined>(undefined)
  const [isCropModalOpen, setIsCropModalOpen] = useState(false)
  const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false)

  const clearSelectedImage = useCallback(() => {
    setSelectedImageWebPath(undefined)
    setSelectedImageObjectUrl((currentObjectUrl) => {
      if (currentObjectUrl) {
        URL.revokeObjectURL(currentObjectUrl)
      }
      return undefined
    })
  }, [])

  const uploadProfilePhoto = useMutation(['putUserImage'], putProfileImage, {
    onSuccess: () => {
      clearSelectedImage()
      setIsCropModalOpen(false)
      queryClient.invalidateQueries(['auth'])
      queryClient.invalidateQueries('getProfile')
      onUploaded?.()
    },
    onError: () => {
      handleToast(toast, 'Error while updating profile picture')
    },
  })

  const getCropImageSrc = useCallback(() => {
    if (selectedImageWebPath) {
      if (Capacitor.isNativePlatform() && selectedImageWebPath.includes('capacitor://')) {
        return Capacitor.convertFileSrc(selectedImageWebPath)
      }
      return selectedImageWebPath
    }

    return selectedImageObjectUrl || ''
  }, [selectedImageObjectUrl, selectedImageWebPath])

  const openCropper = useCallback(
    (file: File, webPath?: string) => {
      clearSelectedImage()
      if (webPath) {
        setSelectedImageWebPath(webPath)
      } else {
        setSelectedImageObjectUrl(URL.createObjectURL(file))
      }
      setIsCropModalOpen(true)
    },
    [clearSelectedImage]
  )

  const selectProfilePhoto = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) {
      fileInputRef.current?.click()
      return
    }

    try {
      const result = await useCamera()
      openCropper(result.file, result.webPath)
    } catch (error) {
      if (isMediaPermissionDeniedError(error)) {
        setIsPermissionModalOpen(true)
        return
      }
      console.error('Error selecting profile picture:', error)
    }
  }, [openCropper])

  const handleFileInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0]
      if (selectedFile) {
        openCropper(selectedFile)
      }
      event.target.value = ''
    },
    [openCropper]
  )

  const closeCropModal = useCallback(() => {
    setIsCropModalOpen(false)
    clearSelectedImage()
  }, [clearSelectedImage])

  const saveCroppedPhoto = useCallback(
    (file: File | null) => {
      if (!file) return
      uploadProfilePhoto.mutate({
        profile_image: file,
      })
    },
    [uploadProfilePhoto]
  )

  useEffect(() => {
    return () => {
      clearSelectedImage()
    }
  }, [clearSelectedImage])

  return {
    fileInputRef,
    isCropModalOpen,
    closeCropModal,
    cropImageSrc: getCropImageSrc(),
    saveCroppedPhoto,
    isUploading: uploadProfilePhoto.isLoading,
    isPermissionModalOpen,
    closePermissionModal: () => setIsPermissionModalOpen(false),
    selectProfilePhoto,
    handleFileInputChange,
  }
}
